import {useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {HistoryOutlined, CheckOutlined, PlusOutlined} from '@ant-design/icons';
import {AnimatePresence, motion} from 'framer-motion';
import {toast} from 'react-toastify';
import styles from './floatingHistoryButton.module.scss';
import DBModal from 'components/modal/modal';
import {useHistoryStore} from '../../zustand/historyStore';
import {useHistoryContextStore} from '../../zustand/historyContextStore';
import {
  buildDashboardSearchIndex,
  resolveEntryForPath,
} from 'utils/dashboardSearchIndex';

/**
 * Global floating "Add to history" button (super-admin only).
 *
 * Saves whatever the admin is on, including the full URL (so tabs/filters kept
 * in the query string are restored) and, when a saveable modal is open, the
 * action needed to re-open it. Labels come from a page/action descriptor when
 * available, else from the dashboard route index. Saving is confirmed first.
 */
const FloatingHistoryButton = () => {
  const location = useLocation();
  const roleName = useSelector(
    state => state?.auth?.userData?.user?.activeAdminTab?.name,
  );
  const adminId = useSelector(state => state?.auth?.userData?.user?.id);
  const isSuperAdmin = roleName === 'SUPERADMIN';

  const addToHistory = useHistoryStore(state => state.addToHistory);
  const pageDescriptor = useHistoryContextStore(state => state.pageDescriptor);
  const actionDescriptor = useHistoryContextStore(
    state => state.actionDescriptor,
  );
  const filtersDescriptor = useHistoryContextStore(
    state => state.filtersDescriptor,
  );
  const index = useMemo(() => buildDashboardSearchIndex(), []);
  const [justSaved, setJustSaved] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const onDashboard = location.pathname.startsWith('/dashboard');
  // Full URL preserves tab/filter query params (e.g. ?route=PENDING).
  const fullPath = `${location.pathname}${location.search}`;

  // Resolve what to save. An open modal (actionDescriptor) wins over the page
  // descriptor, which wins over the route-index label. Descriptors match by
  // pathname only, so they still apply as the query string changes.
  const entryData = useMemo(() => {
    const matchesHere = d => d && d.path === location.pathname;
    // A sub-tab can contribute filters even when its label comes from a shell.
    const contributedFilters = matchesHere(filtersDescriptor)
      ? filtersDescriptor.filters || null
      : null;

    if (matchesHere(actionDescriptor)) {
      return {
        title: actionDescriptor.title,
        subtitle: actionDescriptor.subtitle || '',
        context: actionDescriptor.context || {scope: 'page'},
        action: actionDescriptor.action || null,
        filters: actionDescriptor.filters || contributedFilters,
        meta: actionDescriptor.meta || null,
      };
    }
    if (matchesHere(pageDescriptor)) {
      return {
        title: pageDescriptor.title,
        subtitle: pageDescriptor.subtitle || '',
        context: pageDescriptor.context || {scope: 'page'},
        action: null,
        filters: pageDescriptor.filters || contributedFilters,
        meta: pageDescriptor.meta || null,
      };
    }
    const match = resolveEntryForPath(index, location.pathname);
    if (match) {
      return {
        title: match.label,
        subtitle: match.parent || '',
        context: {scope: 'page', tabKey: match.id, tabLabel: match.label},
        action: null,
        filters: contributedFilters,
        meta: null,
      };
    }
    return {
      title: 'Current page',
      subtitle: '',
      context: {scope: 'page'},
      action: null,
      filters: contributedFilters,
      meta: null,
    };
  }, [
    actionDescriptor,
    pageDescriptor,
    filtersDescriptor,
    location.pathname,
    index,
  ]);

  // "Has filters" is driven purely by real captured filters the page reported —
  // NOT by URL query params (e.g. ?route=PENDING is a tab, not a filter).
  const filterSummary = entryData.filters?.summary || [];
  const filterCount = filterSummary.length;
  const entryHasFilters = filterCount > 0;

  if (!isSuperAdmin || !onDashboard) return null;

  const handleConfirmSave = () => {
    // Shortcuts — including any captured action — are super-admin only.
    if (!isSuperAdmin) return;
    // Identity for de-duping. A ticket is its ticket id; a modal action is its
    // action id. Otherwise it's the full URL PLUS the applied filters — so the
    // same tab with different filters yields DISTINCT shortcuts (added, not
    // overridden), while re-saving the identical view updates in place.
    // The filter fingerprint is order-independent (sorted key=value pairs).
    const filterFingerprint = (entryData.filters?.summary || [])
      .map(chip => `${chip.key}=${chip.value}`)
      .sort()
      .join('&');
    const dedupeKey =
      entryData.context?.scope === 'ticket' && entryData.context?.ticketId
        ? `ticket:${entryData.context.ticketId}`
        : entryData.action?.id
        ? `${entryData.action.type || 'action'}:${entryData.action.id}`
        : `${fullPath}|${filterFingerprint}`;

    addToHistory(adminId, {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      path: fullPath,
      dedupeKey,
      title: entryData.title,
      subtitle: entryData.subtitle,
      context: entryData.context,
      hasFilters: entryHasFilters,
      filterCount,
      filters: entryData.filters,
      meta: entryData.meta,
      action: entryData.action,
      createdAt: Date.now(),
    });
    setConfirmOpen(false);
    toast.success('Added to shortcuts');
    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 1600);
  };

  return (
    <>
      <button
        type="button"
        className={`${styles.fab} ${justSaved ? styles.saved : ''}`}
        onClick={() => setConfirmOpen(true)}
        aria-label="Add this page to shortcuts">
        <span className={styles.fabIcon}>
          <AnimatePresence mode="wait" initial={false}>
            {justSaved ? (
              <motion.span
                key="check"
                initial={{scale: 0, opacity: 0}}
                animate={{scale: 1, opacity: 1}}
                exit={{scale: 0, opacity: 0}}
                transition={{duration: 0.18}}>
                <CheckOutlined />
              </motion.span>
            ) : (
              <motion.span
                key="hist"
                initial={{scale: 0, opacity: 0}}
                animate={{scale: 1, opacity: 1}}
                exit={{scale: 0, opacity: 0}}
                transition={{duration: 0.18}}>
                <HistoryOutlined />
              </motion.span>
            )}
          </AnimatePresence>
        </span>
        <span className={styles.fabLabel}>
          {justSaved ? 'Saved to shortcuts' : 'Add to shortcuts'}
          {!justSaved && <PlusOutlined className={styles.plus} />}
        </span>
      </button>

      <DBModal
        isOpen={confirmOpen}
        zIndex={1600}
        handleClose={() => setConfirmOpen(false)}
        content={
          <div className={styles.confirm}>
            <div className={styles.confirmIcon}>
              <HistoryOutlined />
            </div>
            <h2 className={styles.confirmHeading}>Add to shortcuts?</h2>
            <p className={styles.confirmSub}>
              This saves the exact spot you’re viewing so you can jump back to
              it later.
            </p>

            <div className={styles.preview}>
              <div className={styles.previewRow}>
                <span className={styles.previewLabel}>
                  {entryData.context?.scope === 'ticket' ? 'Issue' : 'Page'}
                </span>
                <span className={styles.previewValue}>{entryData.title}</span>
              </div>
              {entryData.subtitle && (
                <div className={styles.previewRow}>
                  <span className={styles.previewLabel}>
                    {entryData.context?.scope === 'ticket' ? 'Customer' : 'For'}
                  </span>
                  <span className={styles.previewValue}>
                    {entryData.subtitle}
                  </span>
                </div>
              )}
              {entryData.meta?.length > 0 &&
                entryData.meta.map(item => (
                  <div className={styles.previewRow} key={item.key}>
                    <span className={styles.previewLabel}>{item.label}</span>
                    <span className={styles.previewValue}>{item.value}</span>
                  </div>
                ))}
              {entryData.action && (
                <div className={styles.previewRow}>
                  <span className={styles.previewLabel}>Action</span>
                  <span className={styles.previewValue}>
                    Re-opens {entryData.title}
                  </span>
                </div>
              )}
              {filterCount > 0 && (
                <div className={styles.previewFilters}>
                  <span className={styles.previewLabel}>
                    Filters ({filterCount})
                  </span>
                  <div className={styles.previewChips}>
                    {filterSummary.map(chip => (
                      <span key={chip.key} className={styles.previewChip}>
                        <b>{chip.label}</b>
                        {chip.value ? `: ${chip.value}` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className={styles.confirmActions}>
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={() => setConfirmOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className={styles.saveBtn}
                onClick={handleConfirmSave}>
                Add to shortcuts
              </button>
            </div>
          </div>
        }
      />
    </>
  );
};

export default FloatingHistoryButton;
